import { z } from 'zod'

/**
 * Where a photoset stands with respect to the host review.
 *
 * Derived by the backend from the review record and never from the feature
 * flag: during the gradual rollout two shoots side by side legitimately
 * traverse different step sequences, so this has to be asked per photoset.
 *
 * `review_closed` is terminal even when the photoset step still reads
 * `pending_host_review` - that combination is what a full rejection looks like,
 * and the review record wins.
 */
export const HostReviewState = z.enum([
	'not_reviewed',
	'pending_host_review',
	'review_closed'
])
export type HostReviewState = z.infer<typeof HostReviewState>

/**
 * How the review ended. Derived at close from what was recorded - whether the
 * gallery was ever opened and whether anything was removed - never from which
 * button the host pressed.
 *
 * - `approved_all_unopened` - never opened the gallery, removed nothing.
 * - `confirmed_all_reviewed` - opened the gallery, removed nothing.
 * - `submitted_selection` - removed at least one photo.
 * - `auto_closed` - the 72h window expired; every photo is kept.
 */
export const HostReviewOutcome = z.enum([
	'approved_all_unopened',
	'confirmed_all_reviewed',
	'submitted_selection',
	'auto_closed'
])
export type HostReviewOutcome = z.infer<typeof HostReviewOutcome>

/**
 * `approved` is the single folder of a non-reviewed set (today's view); `kept`
 * and `removed` are the two folders of a closed review.
 */
export const HostReviewFolderKind = z.enum(['approved', 'kept', 'removed'])
export type HostReviewFolderKind = z.infer<typeof HostReviewFolderKind>

/** Thumbnails of the retouched rendition, keyed by size. */
export const HostReviewThumbnails = z.record(z.string(), z.string())
export type HostReviewThumbnails = z.infer<typeof HostReviewThumbnails>

/**
 * The outcome context of a review. Everything describing how it ended is null
 * while it is still open, so `closedAt`/`outcome`/the counts must never be read
 * without checking the state first.
 */
export const HostReviewContext = z.object({
	reviewId: z.string(),
	openedAt: z.coerce.date(),
	/** The instant the review auto-closes with every photo kept. */
	deadline: z.coerce.date(),
	closedAt: z.coerce.date().nullable(),
	outcome: HostReviewOutcome.nullable(),
	keptCount: z.number().int().nonnegative().nullable(),
	removedCount: z.number().int().nonnegative().nullable(),
	/** Every photo was removed: nothing of this set is ever uploaded. */
	isFullRejection: z.boolean()
})
export type HostReviewContext = z.infer<typeof HostReviewContext>

/** The photoset read surface the route guard keys on. */
export const PhotosetReviewState = z.object({
	photosetId: z.number().int(),
	/** The backend's own step vocabulary - not the slugs Luminate routes on. */
	stepKey: z.string(),
	qaStepKey: z.string().nullable(),
	reviewState: HostReviewState,
	/** Present whenever a review exists, open or closed; null otherwise. */
	review: HostReviewContext.nullable()
})
export type PhotosetReviewState = z.infer<typeof PhotosetReviewState>

export const HostReviewPhoto = z.object({
	imageId: z.number().int(),
	name: z.string(),
	/** The archive entry name, without extension. */
	fileName: z.string().nullable(),
	thumbnails: HostReviewThumbnails
})
export type HostReviewPhoto = z.infer<typeof HostReviewPhoto>

/**
 * A folder of the uploader view. No action on a photo exists in the contract:
 * a removed photo is shown for reference only.
 */
export const HostReviewFolder = z.object({
	kind: HostReviewFolderKind,
	photoCount: z.number().int().nonnegative(),
	photos: z.array(HostReviewPhoto)
})
export type HostReviewFolder = z.infer<typeof HostReviewFolder>

/** The one download of the view: for a reviewed set this is the kept archive. */
export const HostReviewPackage = z.object({
	url: z.string(),
	builtAt: z.coerce.date()
})
export type HostReviewPackage = z.infer<typeof HostReviewPackage>

export const UploaderFolderView = z.object({
	photosetId: z.number().int(),
	reviewState: HostReviewState,
	review: HostReviewContext.nullable(),
	/**
	 * One `approved` folder for a non-reviewed set, `kept` + `removed` for a
	 * closed review, and empty while the review is open - so folder count can
	 * never stand in for the review state.
	 */
	folders: z.array(HostReviewFolder),
	/** Null while the review is open, on full rejection, or until built. */
	approvedPackage: HostReviewPackage.nullable(),
	/**
	 * The archive of what the host removed. Built only when something was
	 * actually removed, so it is null on a review that kept everything - and it
	 * is the only archive a full rejection has.
	 */
	removedPackage: HostReviewPackage.nullable()
})
export type UploaderFolderView = z.infer<typeof UploaderFolderView>

/** The shoot a full-rejection review belongs to. Mostly nullable by contract. */
export const FullRejectionShoot = z.object({
	taskId: z.number().int(),
	/** The Mediabox identifier the team searches by. */
	taskIdentifier: z.string().nullable(),
	hostId: z.number().int().nullable(),
	hostName: z.string().nullable(),
	listingId: z.number().int().nullable(),
	listingTitle: z.string().nullable(),
	photographerId: z.number().int(),
	photographerName: z.string().nullable()
})
export type FullRejectionShoot = z.infer<typeof FullRejectionShoot>

export const FullRejectionPhoto = z.object({
	imageId: z.number().int(),
	name: z.string(),
	/** The room it was allocated to when reviewed; null when unallocated. */
	roomKey: z.string().nullable(),
	thumbnails: HostReviewThumbnails
})
export type FullRejectionPhoto = z.infer<typeof FullRejectionPhoto>

/**
 * A closed review whose every photo the host removed.
 *
 * Note `rejectionReason` is required here - it is mandatory at submission on a
 * full rejection - even though the same field is nullable on the closed-review
 * summary, where a partial removal carries no reason.
 */
export const FullRejectionListItem = z.object({
	reviewId: z.string(),
	photosetId: z.number().int(),
	closedAt: z.coerce.date(),
	removedCount: z.number().int().nonnegative(),
	/** The mandatory reason exactly as the host submitted it. */
	rejectionReason: z.string(),
	/** The host's note exactly as submitted; null when none was given. */
	note: z.string().nullable(),
	shoot: FullRejectionShoot
})
export type FullRejectionListItem = z.infer<typeof FullRejectionListItem>

export const FullRejectionDetail = FullRejectionListItem.extend({
	/** The editor-selected set in the room order the host saw it. */
	photos: z.array(FullRejectionPhoto)
})
export type FullRejectionDetail = z.infer<typeof FullRejectionDetail>

export const PaginationMeta = z.object({
	total: z.number().int().nonnegative(),
	currentPage: z.number().int().positive(),
	perPage: z.number().int().positive(),
	lastPage: z.number().int().nonnegative()
})
export type PaginationMeta = z.infer<typeof PaginationMeta>

export const FullRejectionList = z.object({
	data: z.array(FullRejectionListItem),
	meta: PaginationMeta
})
export type FullRejectionList = z.infer<typeof FullRejectionList>
